import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Grid,
  TextField,
  MenuItem,
  Typography,
  Alert,
  CircularProgress,
  Chip,
} from '@mui/material';
import Taiwan from '@svg-maps/taiwan';
import athleteService from '../services/athleteService';
import {
  SELECT_COLORS,
  MAX_COMPARE,
  StatCards,
  YearlyChart,
  AthleteTable,
  CompareView,
  sumTotals,
} from './StrengthShared';

// @svg-maps/taiwan 的縣市 id 對應中文名稱（與資料庫 SchoolCity 一致）
const ID_TO_ZH = {
  'changhua-county': '彰化縣',
  'chiayi-city': '嘉義市',
  'chiayi-county': '嘉義縣',
  'hualien-county': '花蓮縣',
  'hsinchu-city': '新竹市',
  'hsinchu-county': '新竹縣',
  'kaohsiung-city': '高雄市',
  'keelung-city': '基隆市',
  'kinmen-county': '金門縣',
  'lienchiang-county': '連江縣',
  'miaoli-county': '苗栗縣',
  'nantou-county': '南投縣',
  'new-taipei-city': '新北市',
  'penghu-county': '澎湖縣',
  'pingtung-county': '屏東縣',
  'taichung-city': '臺中市',
  'tainan-city': '臺南市',
  'taipei-city': '臺北市',
  'taitung-county': '臺東縣',
  'taoyuan-city': '桃園市',
  'yilan-county': '宜蘭縣',
  'yunlin-county': '雲林縣',
};

// 依金牌數在淺藍到深藍之間內插，做地圖著色
const lerpColor = (t) => {
  const from = [232, 238, 247];
  const to = [29, 99, 184];
  const c = from.map((f, i) => Math.round(f + (to[i] - f) * t));
  return `rgb(${c[0]},${c[1]},${c[2]})`;
};

const CountyStrength = ({ allSports, gamesNames, onSearchAthlete }) => {
  const [sport, setSport] = useState('田徑');
  const [gamesName, setGamesName] = useState('');
  const [overview, setOverview] = useState([]);
  const [selected, setSelected] = useState([]); // 最多 MAX_COMPARE 個縣市
  const [details, setDetails] = useState({}); // county -> stats data
  const [hovered, setHovered] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  // 換運動類別或賽事時載入各縣市獎牌總覽（地圖著色）
  useEffect(() => {
    if (!sport) return;
    let mounted = true;
    athleteService
      .getCountyOverview(sport, gamesName || undefined)
      .then((res) => {
        if (mounted && res.success) setOverview(res.data);
      })
      .catch(() => {});
    return () => {
      mounted = false;
    };
  }, [sport, gamesName]);

  // 載入選取縣市的詳細統計
  useEffect(() => {
    if (selected.length === 0) {
      setDetails({});
      setMessage('');
      return;
    }
    let mounted = true;
    setLoading(true);
    setMessage('');
    Promise.all(
      selected.map((county) =>
        athleteService
          .getCountySportStats(county, sport, gamesName || undefined)
          .then((res) => [county, res.success ? res.data : null])
          .catch(() => [county, null])
      )
    )
      .then((entries) => {
        if (!mounted) return;
        const next = {};
        entries.forEach(([county, data]) => {
          if (data) next[county] = data;
        });
        setDetails(next);
        const msgs = entries
          .map(([county, data]) => data?.message)
          .filter(Boolean);
        if (msgs.length > 0) setMessage(msgs.join('；'));
      })
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, [selected, sport, gamesName]);

  const overviewMap = useMemo(() => {
    const m = {};
    overview.forEach((o) => {
      m[o.county] = o;
    });
    return m;
  }, [overview]);

  const maxGold = useMemo(
    () => Math.max(1, ...overview.map((o) => o.gold)),
    [overview]
  );

  const toggleCounty = (county) => {
    setSelected((prev) => {
      if (prev.includes(county)) return prev.filter((c) => c !== county);
      if (prev.length < MAX_COMPARE) return [...prev, county];
      // 已達上限時，替換最後一個
      return [...prev.slice(0, MAX_COMPARE - 1), county];
    });
  };

  const hoveredStats = hovered ? overviewMap[hovered] : null;

  return (
    <Grid container spacing={3}>
      {/* 左欄：控制項與台灣地圖 */}
      <Grid item xs={12} md={5}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <TextField
              select
              fullWidth
              label="賽事"
              value={gamesName}
              onChange={(e) => setGamesName(e.target.value)}
              size="small"
            >
              <MenuItem value="">全部賽事</MenuItem>
              {(gamesNames || []).map((g) => (
                <MenuItem key={g} value={g}>
                  {g}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              fullWidth
              label="運動類別"
              value={allSports.includes(sport) ? sport : ''}
              onChange={(e) => setSport(e.target.value)}
              size="small"
            >
              {allSports.map((s) => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', minHeight: 32 }}>
            {selected.map((county, i) => (
              <Chip
                key={county}
                label={county}
                onDelete={() => toggleCounty(county)}
                sx={{
                  bgcolor: SELECT_COLORS[i],
                  color: '#fff',
                  '& .MuiChip-deleteIcon': { color: 'rgba(255,255,255,0.8)' },
                }}
              />
            ))}
            {selected.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                點擊地圖選擇縣市，最多可選 {MAX_COMPARE} 個互相比較。
              </Typography>
            )}
          </Box>

          <Box sx={{ position: 'relative' }}>
            <svg
              viewBox={Taiwan.viewBox}
              style={{ width: '100%', height: 'auto', display: 'block' }}
              role="img"
              aria-label="台灣縣市地圖"
            >
              {Taiwan.locations.map((loc) => {
                const county = ID_TO_ZH[loc.id];
                const stats = county ? overviewMap[county] : null;
                const selIdx = county ? selected.indexOf(county) : -1;
                const t = stats ? Math.sqrt(stats.gold / maxGold) : 0;
                return (
                  <path
                    key={loc.id}
                    d={loc.path}
                    fill={stats ? lerpColor(t) : '#f0f2f5'}
                    stroke={selIdx >= 0 ? SELECT_COLORS[selIdx] : '#ffffff'}
                    strokeWidth={selIdx >= 0 ? 6 : 1.5}
                    style={{ cursor: county ? 'pointer' : 'default' }}
                    onClick={() => county && toggleCounty(county)}
                    onMouseEnter={() => setHovered(county)}
                    onMouseLeave={() => setHovered(null)}
                  >
                    <title>{county || loc.name}</title>
                  </path>
                );
              })}
            </svg>
            {hoveredStats && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  bgcolor: 'rgba(255,255,255,0.95)',
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1.5,
                  px: 1.5,
                  py: 1,
                  pointerEvents: 'none',
                }}
              >
                <Typography variant="subtitle2">{hoveredStats.county}</Typography>
                <Typography variant="body2" color="text.secondary">
                  金 {hoveredStats.gold}、銀 {hoveredStats.silver}、銅 {hoveredStats.bronze}
                </Typography>
              </Box>
            )}
          </Box>

          <Typography variant="body2" color="text.secondary">
            地圖顏色越深代表該縣市在此運動的金牌數越多，
            統計涵蓋所選賽事：學校依所在縣市歸戶，縣市代表隊直接計入。
          </Typography>
        </Box>
      </Grid>

      {/* 右欄：單一縣市詳情或兩縣市比較 */}
      <Grid item xs={12} md={7}>
        {loading && (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <CircularProgress size={28} />
          </Box>
        )}
        {!loading && message && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {message}
          </Alert>
        )}
        {!loading && selected.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            點擊左側地圖選擇縣市，即可查看該縣市在此運動的歷年表現與主力選手；
            選兩個以上縣市即以折線圖比較。點選手姓名可直接看歷年成績。
          </Typography>
        )}

        {!loading && selected.length === 1 && (() => {
          const county = selected[0];
          const data = details[county];
          if (!data || !data.yearly?.length) return null;
          return (
            <Box>
              <Typography variant="subtitle1" sx={{ mb: 1, color: SELECT_COLORS[0] }}>
                {county} {sport} 歷年獎牌
              </Typography>
              <StatCards totals={sumTotals(data.yearly)} />
              <YearlyChart yearly={data.yearly} height={280} />
              {data.athletes?.length > 0 && (
                <>
                  <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                    主力選手
                  </Typography>
                  <AthleteTable athletes={data.athletes} onSearchAthlete={onSearchAthlete} />
                </>
              )}
            </Box>
          );
        })()}

        {!loading && selected.length >= 2 && (
          <CompareView
            units={selected}
            details={details}
            onSearchAthlete={onSearchAthlete}
          />
        )}
      </Grid>
    </Grid>
  );
};

export default CountyStrength;
